import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  Work,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { documentReviewAPI } from '../../services/api';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();


interface DocumentTemplate {
  id: string;
  title: string;
  description?: string;
  type: 'text' | 'pdf' | 'html' | 'markdown';
  content?: string;
  filePath?: string;
  fileName?: string;
  displayOrder: number;
  isActive: boolean;
  isRequired: boolean;
}

interface DocumentReviewFlowProps {
  memberProfileId: string;
  onComplete: () => void;
  onBack: () => void;
}

export const DocumentReviewFlow: React.FC<DocumentReviewFlowProps> = ({
  memberProfileId,
  onComplete,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<DocumentTemplate[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [acceptedDocuments, setAcceptedDocuments] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [attemptedDecline, setAttemptedDecline] = useState(false);

  useEffect(() => {
    loadPendingDocuments();
  }, [memberProfileId]);

  useEffect(() => {
    if (documents.length > 0 && currentIndex < documents.length) {
      // Skip documents that are already accepted
      const currentDoc = documents[currentIndex];
      if (acceptedDocuments.has(currentDoc.id)) {
        // Move to next document
        if (currentIndex < documents.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          // All documents are already accepted
          onComplete();
        }
      } else {
        loadDocumentContent(documents[currentIndex]);
      }
    }
  }, [currentIndex, documents, acceptedDocuments]);

  const loadPendingDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentReviewAPI.getPendingDocuments(memberProfileId);
      setDocuments(response.data);

      if (response.data.length === 0) {
        // No pending documents, complete immediately
        onComplete();
        return;
      }

      // Load acceptances to properly initialize accepted state
      // This helps handle edge cases where a document was already accepted
      try {
        const acceptancesResponse = await documentReviewAPI.getAcceptances(memberProfileId);
        const acceptedIds = new Set<string>(
          acceptancesResponse.data.map((acceptance: any) => acceptance.documentTemplateId as string)
        );
        setAcceptedDocuments(acceptedIds);
      } catch (acceptError) {
        console.error('Error loading acceptances:', acceptError);
        // Non-fatal, continue with empty set
      }
    } catch (error: any) {
      console.error('Error loading pending documents:', error);
      setError(error.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentContent = async (document: DocumentTemplate) => {
    try {
      setError(null);
      setPageNumber(1); // Reset to first page when loading new document

      if (document.type === 'pdf') {
        // For PDFs, get the download URL and set it for the PDF viewer
        const response = await documentReviewAPI.downloadDocument(document.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setDocumentContent('');
      } else {
        // For other document types, get the content
        const response = await documentReviewAPI.getDocumentContent(document.id);
        setDocumentContent(response.data.content || '');
        setPdfUrl(null);
      }
    } catch (error: any) {
      console.error('Error loading document content:', error);
      setError(error.response?.data?.message || 'Failed to load document content');
    }
  };

  const handleAcceptDocument = async () => {
    const currentDocument = documents[currentIndex];

    try {
      setProcessing(true);
      setError(null);

      await documentReviewAPI.acceptDocument(memberProfileId, currentDocument.id);

      // Mark as accepted
      const newAccepted = new Set(acceptedDocuments);
      newAccepted.add(currentDocument.id);
      setAcceptedDocuments(newAccepted);

      // Move to next document or complete
      if (currentIndex < documents.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // All documents reviewed
        onComplete();
      }
    } catch (error: any) {
      console.error('Error accepting document:', error);
      
      // Handle 409 (already accepted) - treat as success and move forward
      if (error.response?.status === 409) {
        // Mark as accepted
        const newAccepted = new Set(acceptedDocuments);
        newAccepted.add(currentDocument.id);
        setAcceptedDocuments(newAccepted);

        // Move to next document or complete
        if (currentIndex < documents.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          // All documents reviewed
          onComplete();
        }
      } else {
        setError(error.response?.data?.message || 'Failed to accept document');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineDocument = () => {
    const currentDocument = documents[currentIndex];

    if (currentDocument.isRequired) {
      setAttemptedDecline(true);
      setShowDeclineDialog(true);
    } else {
      // For optional documents, allow decline and move to next
      if (currentIndex < documents.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        onComplete();
      }
    }
  };

  const handleDownloadPDF = async () => {
    const currentDocument = documents[currentIndex];

    if (currentDocument.type !== 'pdf') return;

    try {
      const response = await documentReviewAPI.downloadDocument(currentDocument.id);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentDocument.fileName || `${currentDocument.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      setError(error.response?.data?.message || 'Failed to download document');
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setAttemptedDecline(false);
      setScale(1.0); // Reset zoom
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset: number) => {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      changePage(-1);
    }
  };

  const nextPage = () => {
    if (numPages && pageNumber < numPages) {
      changePage(1);
    }
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 2.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.6));
  };

  // Cleanup PDF URL when component unmounts or document changes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (documents.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="success">
          All required documents have been reviewed and accepted!
        </Alert>
      </Box>
    );
  }

  const currentDocument = documents[currentIndex];
  const progress = ((currentIndex) / documents.length) * 100;
  const completedProgress = ((acceptedDocuments.size) / documents.length) * 100;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Progress */}
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Document Review
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Viewing document {currentIndex + 1} of {documents.length}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onBack}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box mb={1}>
          <LinearProgress
            variant="determinate"
            value={completedProgress}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          {acceptedDocuments.size} of {documents.length} documents accepted
        </Typography>
      </Box>

      <Divider />

      {/* Document Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {attemptedDecline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This document is required for account setup. You must accept it to proceed.
          </Alert>
        )}

        <Typography variant="h5" gutterBottom fontWeight={600}>
          {currentDocument.title}
        </Typography>

        {currentDocument.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {currentDocument.description}
          </Typography>
        )}

        {currentDocument.isRequired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This document is required for account setup
          </Alert>
        )}

        {currentDocument.type === 'pdf' && pdfUrl ? (
          <Box>
            {/* PDF Controls */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={zoomOut} disabled={scale <= 0.6} size="small">
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <IconButton onClick={zoomIn} disabled={scale >= 2.0} size="small">
                  <ZoomInIcon />
                </IconButton>
              </Box>

              {numPages && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    size="small"
                    onClick={previousPage}
                    disabled={pageNumber <= 1}
                    variant="outlined"
                  >
                    Previous
                  </Button>
                  <Typography variant="body2">
                    Page {pageNumber} of {numPages}
                  </Typography>
                  <Button
                    size="small"
                    onClick={nextPage}
                    disabled={pageNumber >= numPages}
                    variant="outlined"
                  >
                    Next
                  </Button>
                </Box>
              )}

              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                variant="outlined"
              >
                Download
              </Button>
            </Box>

            {/* PDF Viewer */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: '600px',
                overflow: 'auto',
                bgcolor: 'grey.50',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error: any) => {
                  if (error?.status !== 0) {
                    console.error('Error loading PDF:', error);
                    setError('Failed to load PDF document');
                  }
                }}
                loading={
                  <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                  </Box>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            </Paper>
          </Box>
        ) : currentDocument.type === 'pdf' ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mt: 2,
              maxHeight: '600px',
              overflow: 'auto',
              bgcolor: 'grey.50'
            }}
          >
            {currentDocument.type === 'html' ? (
              <div dangerouslySetInnerHTML={{ __html: documentContent }} />
            ) : (
              <Typography
                variant="body1"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  m: 0
                }}
              >
                {documentContent}
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      <Divider />

      {/* Action Buttons */}
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={currentIndex === 0 ? onBack : handlePrevious}
              disabled={processing}
              color="primary"
              sx={{ 
                border: 1, 
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <BackIcon />
            </IconButton>
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleDeclineDocument}
              disabled={processing}
              size="small"
              sx={{ 
                minWidth: { xs: 'auto', sm: '100px' },
                px: { xs: 1, sm: 2 }
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleAcceptDocument}
              disabled={processing}
              size="small"
              sx={{ 
                minWidth: { xs: 'auto', sm: '120px' },
                px: { xs: 1, sm: 2 }
              }}
            >
              {processing ? (
                'Processing...'
              ) : (
                <>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Accept & Continue
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Accept
                  </Box>
                </>
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Decline Required Document Dialog */}
      <Dialog
        open={showDeclineDialog}
        onClose={() => setShowDeclineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Required Document
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This document is required for account setup
          </Alert>
          <Typography variant="body1" paragraph>
            You must accept "{currentDocument.title}" to complete your account setup and access club features.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you have concerns about this document, please contact club administration for assistance.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeclineDialog(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowDeclineDialog(false);
              setAttemptedDecline(false);
            }}
          >
            Review Again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
